import { Copy, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Agent } from "@/types/agent/agent";

interface PhoneAccessSectionProps {
  genie: Agent;
  onConfigurePhone: () => void;
}

export default function PhoneAccessSection({ genie, onConfigurePhone }: PhoneAccessSectionProps) {
  const numbers = [
    genie.phone?.number ? { label: "Genie Number", value: genie.phone.number } : null,
    genie.transfer_phone?.number ? { label: "Transfer Number", value: genie.transfer_phone.number } : null,
  ].filter((n): n is { label: string; value: string } => n !== null);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Phone number copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  if (numbers.length === 0) {
    return (
      <div className="pt-4">
        <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Phone className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700 mb-1">No phone numbers attached</p>
          <p className="text-sm text-gray-500 mb-4">Attach a phone number so people can call this genie directly.</p>
          <Button onClick={onConfigurePhone} size="sm">
            Configure phone number
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-4 space-y-3">
      {numbers.map((number) => (
        <div
          key={number.label}
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
        >
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{number.label}</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">{number.value}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => handleCopy(number.value)}>
            <Copy className="w-4 h-4 mr-1.5" />
            Copy
          </Button>
        </div>
      ))}
      <button
        onClick={onConfigurePhone}
        className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors text-left px-1"
      >
        Manage phone numbers →
      </button>
    </div>
  );
}
